import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CurrencyType, OfferingPriceDto, OfferingAgePricingRange } from '@/domain/offerings/offering'
import { addOfferingPriceSchema, addOfferingPriceWithAgesSchema } from '@/domain/offerings/schemas'
import { useAddPrice } from '@/hooks/api/useOfferings'
import { useOfferingAgePricing } from '@/hooks/api/useOfferingAgePricing'
import { zodResolver } from '@hookform/resolvers/zod'
import { addDays } from 'date-fns'
import { Loader2 } from 'lucide-react'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import DatePicker from '../shared/DatePicker'
import { AgePricingRangeForm } from './AgePricingRangeForm'

interface OfferingPriceFormProps {
  offeringId: string
  currency?: CurrencyType
  hasAgePricing?: boolean
  onSuccess?: () => void
  onCancel?: () => void
}

const defaultValues = {
  price: 0,
  currency: CurrencyType.ARS,
  effectiveFrom: addDays(new Date(), 1),
  effectiveUntil: undefined,
  agePricingRanges: [] as OfferingAgePricingRange[],
}

export const OfferingPriceForm = ({
  offeringId,
  currency = CurrencyType.ARS,
  hasAgePricing = false,
  onSuccess,
  onCancel
}: OfferingPriceFormProps) => {
  const { mutateAsync: addPrice, isPending } = useAddPrice()
  const { data: currentAgePricing } = useOfferingAgePricing(offeringId)
  const schema = hasAgePricing ? addOfferingPriceWithAgesSchema() : addOfferingPriceSchema()

  const form = useForm<OfferingPriceDto>({
    resolver: zodResolver(schema),
    defaultValues: {
      ...defaultValues,
      price: hasAgePricing ? 1 : 0,
      currency
    },
    mode: 'onBlur',
  })

  useEffect(() => {
    if (currentAgePricing) {
      form.setValue('agePricingRanges', currentAgePricing.ranges)
    }
  }, [currentAgePricing, form])

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await addPrice({
        offeringId,
        price: {
          price: values.price,
          currency: values.currency,
          effectiveFrom: values.effectiveFrom,
          effectiveUntil: values.effectiveUntil,
          agePricingRanges: hasAgePricing ? values.agePricingRanges : undefined,
        },
      })
      onSuccess?.()
    } catch {
      // Error handled by hook
    }
  })

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {!hasAgePricing && <div className="space-y-2">
          <Label htmlFor="price">Precio Base ({currency})</Label>
          <Input
            id="price"
            type="number"
            step="0.01"
            placeholder="0.00"
            className="h-11"
            {...form.register('price', { valueAsNumber: true })}
            onFocus={(e) => e.target.select()}
          />
          {form.formState.errors.price && (
            <p className="text-xs text-destructive">{form.formState.errors.price.message}</p>
          )}
        </div>}

        <Controller
          control={form.control}
          name="effectiveFrom"
          render={({ field }) => (
            <div className="space-y-2">
              <Label>Efectivo desde</Label>
              <DatePicker
                date={field.value}
                onSelect={(date) => field.onChange(date)}
              />
              {form.formState.errors.effectiveFrom && (
                <p className="text-xs text-destructive">{form.formState.errors.effectiveFrom.message}</p>
              )}
            </div>
          )}
        />

        <Controller
          control={form.control}
          name="effectiveUntil"
          render={({ field }) => (
            <div className="space-y-2">
              <Label>Efectivo hasta (opcional)</Label>
              <div className="flex items-center space-x-2">
                <DatePicker
                  date={field.value}
                  onSelect={(date) => field.onChange(date)}
                />
                {field.value && (
                  <Button type="button" variant="outline" size="sm" onClick={() => field.onChange(undefined)}>
                    Limpiar
                  </Button>
                )}
              </div>
              {form.formState.errors.effectiveUntil && (
                <p className="text-xs text-destructive">{form.formState.errors.effectiveUntil.message}</p>
              )}
            </div>
          )}
        />
      </div>

      {hasAgePricing && (
        <Controller
          control={form.control}
          name="agePricingRanges"
          render={({ field }) => (
            <div className="space-y-4 pt-6 border-t">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold text-primary">Configuración de Precios por Edad</Label>
                <p className="text-xs text-muted-foreground italic">
                  Estos rangos se aplicarán para el nuevo periodo de precios.
                </p>
              </div>
              <AgePricingRangeForm
                ranges={field.value || []}
                onChange={(ranges) => field.onChange(ranges)}
              />
            </div>
          )}
        />
      )}

      <div className="flex justify-between gap-3 pt-6 border-t">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
          Cancelar
        </Button>
        <Button type="submit" size="lg" className="" disabled={isPending}>
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Guardar
        </Button>
      </div>
    </form>
  )
}
